import { formatParisDateTime, type CantonScan } from '@flux/shared';
import { Loader2, RotateCw } from 'lucide-react';
import { useId, type JSX } from 'react';
import { Checkbox } from '@/components/ui/checkbox.js';
import { Button } from '@/components/ui/button.js';
import { ConfirmDialog } from '@/components/ui/confirm-dialog.js';
import { DescriptionItem, DescriptionList } from '@/components/ui/description-list.js';
import { formatCount, formatCountLabel } from './money.js';

/**
 * Vérification de toutes les adresses suisses (`06` §3.3, écran `06` §8.2) :
 * ligne de suivi, rapport repliable et confirmation de lancement.
 */

function count(scan: CantonScan, key: string): number {
  return scan.counts[key] ?? 0;
}

/** Ligne de suivi (`role="status"`, annoncée à chaque rafraîchissement du polling). */
export function CantonScanStatusLine({
  scan,
  onRetry,
  canRetry,
  loadError,
  onReload,
}: {
  scan: CantonScan | null;
  onRetry: () => void;
  canRetry: boolean;
  /** Échec de lecture du suivi : message à afficher avec « Réessayer ». */
  loadError: string | null;
  onReload: () => void;
}): JSX.Element {
  if (loadError !== null && scan === null) {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <p role="alert" className="text-status-red-fg">
          {loadError}
        </p>
        <Button type="button" variant="ghost" size="sm" onClick={onReload}>
          <RotateCw aria-hidden="true" />
          Réessayer
        </Button>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <div role="status" className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        {scan === null ? null : scan.status === 'running' ? (
          <p className="flex items-center gap-2 text-foreground">
            <Loader2 className="size-4 animate-spin text-muted-foreground" aria-hidden="true" />
            <span className="tabular-nums">
              {`Vérification en cours${scan.mode === 'simulation' ? ' (simulation)' : ''} · ${formatCountLabel(
                count(scan, 'read'),
                'adresse lue',
                'adresses lues',
              )} · ${formatCountLabel(count(scan, 'updated') + count(scan, 'simulated'), 'corrigée', 'corrigées')}`}
            </span>
          </p>
        ) : scan.status === 'succeeded' ? (
          <p className="tabular-nums text-muted-foreground">
            {`Dernière vérification le ${formatParisDateTime(new Date(scan.finishedAt ?? scan.startedAt))}${
              scan.mode === 'simulation' ? ' (simulation)' : ''
            } · ${formatCountLabel(count(scan, 'updated'), 'corrigée', 'corrigées')} · ${formatCountLabel(
              count(scan, 'simulated'),
              'simulée',
              'simulées',
            )} · ${formatCount(count(scan, 'unresolved'))} à traiter`}
          </p>
        ) : (
          <p className="text-status-red-fg">
            {`La vérification du ${formatParisDateTime(new Date(scan.startedAt))} a échoué : ${
              scan.error ?? 'relancez-la, les adresses déjà corrigées seront ignorées.'
            }`}
          </p>
        )}
      </div>
      {scan?.status === 'failed' ? (
        <div>
          <Button type="button" variant="outline" size="sm" disabled={!canRetry} onClick={onRetry}>
            <RotateCw aria-hidden="true" />
            Réessayer
          </Button>
        </div>
      ) : null}
      {scan !== null && scan.status !== 'running' ? <CantonScanReport scan={scan} /> : null}
    </div>
  );
}

const REPORT_ROWS: readonly { key: string; label: string }[] = [
  { key: 'read', label: 'Adresses lues' },
  { key: 'swiss', label: 'Adresses suisses' },
  { key: 'updated', label: 'Corrigées' },
  { key: 'simulated', label: 'Simulées' },
  { key: 'skipped', label: 'Ignorées (canton déjà présent)' },
  { key: 'unresolved', label: 'À traiter' },
  { key: 'resolvedManually', label: 'Résolues à la main' },
  { key: 'errors', label: 'Erreurs' },
];

/** Rapport complet de la dernière vérification, replié sous la ligne de suivi. */
function CantonScanReport({ scan }: { scan: CantonScan }): JSX.Element {
  const formats = Object.entries(scan.provinceFormats).sort((a, b) => b[1] - a[1]);
  return (
    <details className="group text-sm">
      <summary className="w-fit cursor-pointer select-none rounded-sm text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        Voir le rapport
      </summary>
      <div className="mt-3 flex flex-col gap-4 rounded-md border border-border bg-card p-4">
        <DescriptionList>
          <DescriptionItem label="Mode">
            {scan.mode === 'simulation' ? 'Simulation, rien écrit chez Recharge' : 'Écriture chez Recharge'}
          </DescriptionItem>
          <DescriptionItem label="Lancée le">
            <span className="whitespace-nowrap tabular-nums">
              {formatParisDateTime(new Date(scan.startedAt))}
            </span>
            {scan.startedBy === null ? null : ` par ${scan.startedBy.name}`}
          </DescriptionItem>
          {REPORT_ROWS.map((row) => (
            <DescriptionItem key={row.key} label={row.label}>
              <span className="tabular-nums">{formatCount(count(scan, row.key))}</span>
            </DescriptionItem>
          ))}
        </DescriptionList>
        <div className="flex flex-col gap-2 border-t border-border pt-3">
          <h3 className="text-sm font-semibold text-foreground">Formats de canton existants</h3>
          {formats.length === 0 ? (
            <p className="text-muted-foreground">Aucune adresse suisse avec un canton</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {formats.map(([value, total]) => (
                <li key={value} className="flex gap-2">
                  <span className="min-w-0 break-words" translate="no">
                    {value}
                  </span>
                  <span className="tabular-nums text-muted-foreground">{formatCount(total)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </details>
  );
}

export interface CantonScanConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  simulation: boolean;
  onSimulationChange: (simulation: boolean) => void;
  /** Mode test global : la simulation est imposée. */
  forcedSimulation: boolean;
  isStarting: boolean;
  /** Message du serveur quand le lancement échoue (409 : déjà en cours, token absent). */
  error: string | null;
  onConfirm: () => void;
}

/** Confirmation non destructive : nomme la durée et le comportement, propose la simulation. */
export function CantonScanConfirmDialog({
  open,
  onOpenChange,
  simulation,
  onSimulationChange,
  forcedSimulation,
  isStarting,
  error,
  onConfirm,
}: CantonScanConfirmDialogProps): JSX.Element {
  const noteId = useId();
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Vérifier toutes les adresses suisses ?"
      description={
        <div className="flex flex-col gap-3">
          <p>
            Flux parcourt toutes les adresses Recharge (environ 1 requête par seconde, plusieurs
            minutes) et complète le canton des adresses suisses qui n’en ont pas. Relançable sans
            risque.
          </p>
          <div className="flex flex-col gap-1">
            <label className="flex items-center gap-2 text-sm text-foreground">
              <Checkbox
                checked={simulation}
                disabled={forcedSimulation}
                aria-describedby={forcedSimulation ? noteId : undefined}
                onCheckedChange={(checked) => {
                  onSimulationChange(checked === true);
                }}
              />
              Simulation : ne rien écrire chez Recharge
            </label>
            {forcedSimulation ? (
              <p id={noteId} className="pl-6 text-xs text-muted-foreground">
                Le mode test de Flux force la simulation
              </p>
            ) : null}
          </div>
          {error === null ? null : (
            <p role="alert" className="text-sm text-status-red-fg">
              {error}
            </p>
          )}
        </div>
      }
      confirmLabel="Lancer la vérification"
      confirmVariant="default"
      isConfirming={isStarting}
      onConfirm={onConfirm}
    />
  );
}
